// @flow

import type { CategoryType } from '../flowTypes'
import { getLibrary } from '../services/dipseaApi'
import { cloneDeep } from 'lodash'
import type { TrackPropsType } from './Track'
import { convertTrackToProps } from './Track'
import { sortSlugs } from './helpers'
import { filterSeriesWithRepeatingTracks } from './Tag'

export async function getCategorySlugsAsync (): Promise<string[]> {
  try {
    const library = await getLibrary()
    if (!library || !library.slugs || !library.slugs.contexts) {
      return []
    }
    return Object.keys(library.slugs.contexts)
  } catch (error) {
    console.error('Failed to get category slugs:', error)
    return []
  }
}

export async function getCategoryBySlugAsync (slug: string): Promise<CategoryType> {
  const library = await getLibrary()
  const categoryId = library?.slugs?.contexts?.[slug]
  const category = cloneDeep(library?.contexts?.[categoryId] || {})
  return category
}

export async function getCategoryByIdAsync (id: string): Promise<CategoryType> {
  const library = await getLibrary()
  const category = cloneDeep(library?.contexts?.[id] || {})
  return category
}

export async function getAllCategoriesByTypeAsync (type: 'stories' | 'wellness' | 'sleep' | 'hunks' | 'creator'): Promise<CategoryType[]> {
  try {
    const library = await getLibrary()
    if (!library || !library.contexts) {
      return []
    }
    // $FlowFixMe
    const contextIds: string[] = Object.keys(library.contexts)
    return contextIds
      .filter((key: string): boolean => library.contexts?.[key]?.subType === type)
      .sort((a: string, b: string): number => (library.contexts?.[a]?.index || 0) - (library.contexts?.[b]?.index || 0))
      .map((key: string): CategoryType => library.contexts?.[key])
      .filter(Boolean)
  } catch (error) {
    console.error('Failed to get categories by type:', error)
    return []
  }
}

export async function getSlugsByCategoryTypeAsync (type: 'stories' | 'wellness' | 'sleep' | 'hunks' | 'creator'): Promise<string[]> {
  try {
    const library = await getLibrary()
    const creators = await getAllCategoriesByTypeAsync(type)
    const slugs = []
    if (library?.slugs?.contexts) {
      Object.keys(library.slugs.contexts).forEach((slug: string): void => {
        if (creators.find((creator: CategoryType): boolean => creator.id === library.slugs?.contexts?.[slug])) {
          slugs.push(slug)
        }
      })
    }
    return slugs
  } catch (error) {
    console.error('Failed to get slugs by category type:', error)
    return []
  }
}

export function getEpisodeInformation (category: CategoryType, label: string): string {
  const episodeLabel = label || 'Episode'
  const totalStories = Object.keys(category.track_index || {}).length
  return `${totalStories} ${totalStories === 1 ? episodeLabel : episodeLabel + 's'}`
}

export const newReleasesCategory = '-LaxN3RylGGo5jXU4NTh'

export type NewReleasesPropsType = {
  title: 'New Releases',
  tracks: TrackPropsType[]
}
export async function getNewReleasesPropsAsync (): Promise<NewReleasesPropsType> {
  const library = await getLibrary()
  let trackIds = Object.keys(library?.contexts?.[newReleasesCategory]?.track_index || {})
    .sort((a: string, b: string): number => {
      const trackIndex = library?.contexts?.[newReleasesCategory]?.track_index || {}
      return (trackIndex[a]?.index || 0) - (trackIndex[b]?.index || 0)
    })
  trackIds = filterSeriesWithRepeatingTracks(library?.tracks || {}, library?.series || {}, trackIds)
  const tracks = trackIds.filter((trackId: string): boolean => !!(library?.tracks?.[trackId]))
    .map((trackId: string): TrackPropsType => convertTrackToProps(library?.tracks?.[trackId], library))
    .filter((track: TrackPropsType | null): boolean => !!track)
  return {
    title: 'New Releases',
    tracks
  }
}

export async function getCategorySlugAsync (id: string): Promise<string | null> {
  const library = await getLibrary()
  const category = library?.contexts?.[id]
  if (category?.subType === 'hunks') {
    return '/characters/' + sortSlugs(category.slugs)[0]
  }
  return null
}
